import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import {object} from 'yup';

import {useUpdateContactDetails} from '#apollo/schema';
import {Button, Form, Modal, Text} from '#component/base';
import {NumberInputField} from '#component/partial';
import {useToastQueue} from '#hook';
import {sprinkles} from '#style/sprinkles';
import {handleValidationError, phoneValidator} from '#util/validator';
import {ValidationError} from '#util/validator/handleValidationError';

import {EditPhoneNumberModalProps, FieldValues} from './EditPhoneNumberModal.types';

export const EditPhoneNumberModal = ({phoneNumber = '', onSuccess}: EditPhoneNumberModalProps) => {
  const {addToast} = useToastQueue();
  const [updatePhoneNumber] = useUpdateContactDetails();
  const {
    clearErrors,
    formState: {errors, isSubmitting},
    handleSubmit,
    register,
    setError,
    reset: resetForm,
  } = useForm({
    defaultValues: {form: '', phoneNumber: ''},
    mode: 'onBlur',
    resolver: yupResolver(
      object({phoneNumber: phoneValidator.required('Please enter your phone number')}),
    ),
  });

  const handleUpdatePhoneNumber = async ({phoneNumber}: FieldValues) => {
    try {
      await updatePhoneNumber({variables: {input: {phoneNumber}}});
      addToast({children: 'Phone number updated!'});
      onSuccess?.();
    } catch (error) {
      handleValidationError<FieldValues>({
        error: error as unknown as ValidationError<FieldValues>,
        errorMessages: {
          phoneNumber: {
            DUPLICATE: 'An account with this phone number already exists',
            INVALID: 'Please enter a valid phone number',
            MISMATCH: 'An account with this phone number already exists',
          },
          form: 'Unable to update your phone number. Please check the entered data and try again',
        },
        setError,
      });
    }
  };

  return (
    <Modal onCloseAutoFocus={() => resetForm()}>
      <div className={sprinkles({flex: 'flex', dir: 'col', gap: 32})}>
        <Text variant="heading2" color="burgundy">
          Edit phone number
        </Text>

        <Text as="p">
          Updating your phone number will affect any future SMS notifications. You may update this
          at any point.
        </Text>

        <Form
          clearErrors={clearErrors}
          errors={errors}
          onSubmit={handleSubmit(handleUpdatePhoneNumber)}>
          <NumberInputField
            disabled
            label="Current phone Number"
            mask="999-999-9999"
            maskPlaceholder="xxx-xxx-xxxx"
            name="currentPhoneNumber"
            placeholder="xxx-xxx-xxxx"
            type="phone"
            value={phoneNumber}
          />

          <NumberInputField
            errors={errors}
            label="New phone number"
            mask="999-999-9999"
            maskPlaceholder="xxx-xxx-xxxx"
            placeholder="xxx-xxx-xxxx"
            type="phone"
            {...register('phoneNumber')}
          />

          <Button disabled={isSubmitting} type="submit">
            Update
          </Button>
        </Form>
      </div>
    </Modal>
  );
};
